import { Base64 } from 'js-base64';

import { addAnonIdToParams } from '../../../hooks/utils/with-anon-id';

import type { OnboardingData } from './types';

type AnalyticsClientLike = { getAnonymousId: () => string } | null;

type BuildLoomSsoUrlArgs = {
	isProduction: boolean;
	cloudId: string;
	onboardingData: OnboardingData;
	analyticsClient: AnalyticsClientLike;
	/**
	 * The current page's search string. Defaults to `window.location.search`.
	 * Injectable for unit tests and any SSR-adjacent callers.
	 */
	search?: string;
};

/**
 * Decode the base64-JSON `data` blob that Identity places on the confirmation
 * page URL. Returns an empty object when the param is missing or malformed so
 * callers can merge unconditionally.
 */
const decodeIdentityDataParam = (currentSearchParams: URLSearchParams): Record<string, unknown> => {
	const raw = currentSearchParams.get('data');
	if (!raw) {
		return {};
	}
	try {
		const parsed = JSON.parse(Base64.decode(raw));
		return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
	} catch {
		return {};
	}
};

/**
 * Build the Loom SSO deep-link used to hand a freshly-provisioned user off to
 * their Loom workspace.
 *
 * The `data` query param is a base64-encoded JSON blob owned by Identity
 * (contains `flowTraceId`, `login_hint`, `application`, etc.). We MERGE our
 * `onboarding-data` payload into that blob rather than overwriting it, so the
 * Identity/auth handshake on Loom's side still has what it needs.
 *
 * Behaviour is identical in production and stage other than the host.
 */
export const buildLoomSsoUrl = ({
	isProduction,
	cloudId,
	onboardingData,
	analyticsClient,
	search = typeof window !== 'undefined' ? window.location.search : '',
}: BuildLoomSsoUrlArgs): string => {
	const authHost = isProduction ? 'https://loom.com' : 'https://stage.loom.com';
	// Prod puts the workspace on `www.loom.com` whereas auth doesn't
	const workspaceHost = isProduction ? 'https://www.loom.com' : 'https://stage.loom.com';

	const currentSearchParams = new URLSearchParams(search);
	const identityData = decodeIdentityDataParam(currentSearchParams);

	const mergedData = {
		...identityData,
		'onboarding-data': onboardingData,
	};

	const params: Record<string, string> = {
		redirect_after: `${workspaceHost}/workspace/cloud/${cloudId}`,
		data: Base64.encode(JSON.stringify(mergedData)),
	};

	// Preserve signup attribution when the confirmation page carries a `source`.
	const loomSource = currentSearchParams.get('source');
	if (loomSource) {
		params['source'] = loomSource;
	}

	const finalParams = addAnonIdToParams(params, analyticsClient);
	return `${authHost}/api/auth/atlassian?${new URLSearchParams(finalParams).toString()}`;
};
